/**
 * Stripe Service
 *
 * Handles all Stripe-related API calls
 */

import apiService from './apiService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8002';

class StripeService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/billing/stripe`;
  }

  /**
   * Same as apiService: Token + X-Frontend-URL for Stripe / billing
   */
  getAuthHeaders() {
    return apiService.getAuthHeaders();
  }

  /**
   * Handle API errors
   */
  handleError(error, defaultMessage) {
    console.error(defaultMessage, error);
    throw error;
  }

  /**
   * Get Stripe publishable key
   */
  async getPublishableKey() {
    try {
      const response = await fetch(`${this.baseURL}/config/`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await apiService.parseJsonResponse(response);
      return data.publishable_key;
    } catch (error) {
      this.handleError(error, 'Failed to get Stripe publishable key');
    }
  }

  /**
   * Create a checkout session for subscription
   *
   * @param {number} planId - ID of the plan to subscribe to
   * @param {string} successUrl - URL to redirect after successful payment
   * @param {string} cancelUrl - URL to redirect if payment is canceled
   * @param {string} [organizationId] - UUID of the org to upgrade. REQUIRED
   *   when the user is a member of more than one org. Without it, the
   *   backend used to silently default to the first org the user joined,
   *   which charged the wrong workspace (2026-05-08 incident — Kael's
   *   upgrade landed on carlos pena's Workspace).
   */
  async createCheckoutSession(planId, successUrl, cancelUrl, organizationId) {
    try {
      const body = {
        plan_id: planId,
        success_url: successUrl,
        cancel_url: cancelUrl,
      };
      if (organizationId) body.organization_id = organizationId;
      const response = await fetch(`${this.baseURL}/create-checkout-session/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(body),
      });
      return await apiService.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to create checkout session');
    }
  }

  /**
   * Create a payment intent for one-time payment
   *
   * @param {number} amount - Amount in cents (e.g., 1000 for $10.00)
   * @param {string} currency - Currency code (default: 'usd')
   */
  async createPaymentIntent(amount, currency = 'usd') {
    try {
      const response = await fetch(`${this.baseURL}/create-payment-intent/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          amount,
          currency,
        }),
      });
      return await apiService.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to create payment intent');
    }
  }

  /**
   * Cancel a subscription
   *
   * @param {number} subscriptionId - ID of the subscription to cancel
   * @param {boolean} cancelAtPeriodEnd - If true, cancel at end of billing period
   */
  async cancelSubscription(subscriptionId, cancelAtPeriodEnd = true) {
    try {
      const response = await fetch(`${this.baseURL}/cancel-subscription/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          subscription_id: subscriptionId,
          cancel_at_period_end: cancelAtPeriodEnd,
        }),
      });
      return await apiService.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to cancel subscription');
    }
  }

  /**
   * Get subscription status from Stripe
   *
   * @param {number} subscriptionId - ID of the subscription
   */
  async getSubscriptionStatus(subscriptionId) {
    try {
      const response = await fetch(`${this.baseURL}/subscription/${subscriptionId}/status/`, {
        headers: this.getAuthHeaders(),
      });
      return await apiService.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to get subscription status');
    }
  }

  // ==========================================================================
  // Solana gRPC add-on — separate Stripe product from the JSON-RPC subscription.
  // See backend billing/stripe_views.py, endpoints at /api/billing/stripe/grpc-addon/*
  // ==========================================================================

  /**
   * Fetch the org's current Solana gRPC add-on state.
   * Returns { active: false, state: 'never_subscribed' } if the org never
   * subscribed, or a full addon-shape object otherwise.
   */
  async getGrpcAddonStatus(organizationId) {
    if (!organizationId) throw new Error('organizationId is required');
    try {
      const response = await fetch(
        `${this.baseURL}/grpc-addon/?organization_id=${encodeURIComponent(organizationId)}`,
        { headers: this.getAuthHeaders() },
      );
      return await apiService.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to get Solana gRPC add-on status');
    }
  }

  /**
   * Start a Stripe Checkout Session for the Solana gRPC Starter add-on.
   * Redirect the caller to the returned url; the webhook provisions the
   * SolanaGrpcAddon row and flips gateway access after payment.
   */
  async createGrpcAddonCheckoutSession(organizationId, successUrl, cancelUrl) {
    if (!organizationId) throw new Error('organizationId is required');
    try {
      const response = await fetch(`${this.baseURL}/grpc-addon/checkout/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          organization_id: organizationId,
          success_url: successUrl,
          cancel_url: cancelUrl,
        }),
      });
      return await apiService.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to create gRPC add-on checkout session');
    }
  }

  /**
   * Cancel the org's Solana gRPC add-on. Stripe cancel_at_period_end=true —
   * customer keeps access until end of paid period. CLI-granted addons
   * (no stripe_subscription_id) are just flipped to inactive locally.
   */
  async cancelGrpcAddon(organizationId) {
    if (!organizationId) throw new Error('organizationId is required');
    try {
      const response = await fetch(`${this.baseURL}/grpc-addon/cancel/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ organization_id: organizationId }),
      });
      return await apiService.parseJsonResponse(response);
    } catch (error) {
      this.handleError(error, 'Failed to cancel Solana gRPC add-on');
    }
  }
}

const stripeService = new StripeService();
export default stripeService;
